import { useEffect, useRef, useState } from "react";
import { Copy, FileText, Pause, Play, Trash2, WifiOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { openLogStream, type LogMessage } from "@/lib/api";

type Props = {
  slug: string;
  /** Altezza area log (es. "320px", "40vh") */
  height?: string;
  /** Max righe in memoria (default 1000) */
  maxLines?: number;
};

type ConnState = "connecting" | "open" | "closed" | "error";

export function LogViewer({ slug, height = "360px", maxLines = 1000 }: Props) {
  const [lines, setLines] = useState<string[]>([]);
  const [meta, setMeta] = useState<{ exists: boolean; path: string } | null>(
    null,
  );
  const [state, setState] = useState<ConnState>("connecting");
  const [paused, setPaused] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync pausedRef
  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  // Connessione + reconnect
  useEffect(() => {
    let closed = false;

    function connect() {
      if (closed) return;
      setState("connecting");
      const ws = openLogStream(
        slug,
        (msg: LogMessage) => {
          if (msg.type === "meta") {
            setMeta({ exists: msg.exists, path: msg.path });
            return;
          }
          if (msg.type === "error") {
            setState("error");
            return;
          }
          if (msg.type === "line") {
            if (pausedRef.current) return;
            setLines((prev) => {
              const next = [...prev, msg.text];
              return next.length > maxLines
                ? next.slice(next.length - maxLines)
                : next;
            });
          }
        },
        () => {
          setState("closed");
          // Reconnect dopo 2s se non stiamo smontando
          if (!closed) {
            reconnectTimerRef.current = setTimeout(connect, 2000);
          }
        },
      );
      ws.onopen = () => setState("open");
      ws.onerror = () => setState("error");
      wsRef.current = ws;
    }

    connect();

    return () => {
      closed = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close();
    };
  }, [slug, maxLines]);

  // Autoscroll
  useEffect(() => {
    if (paused) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [lines, paused]);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
    } catch {
      // ignore
    }
  };

  const connDot = {
    connecting: "bg-amber-500 animate-pulse",
    open: "bg-moving-500",
    closed: "bg-slate-400",
    error: "bg-red-500",
  }[state];

  const connLabel = {
    connecting: "connessione…",
    open: "live",
    closed: "chiuso",
    error: "errore",
  }[state];

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-slate-950 text-slate-100">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 border-b border-slate-800 bg-slate-900 px-3 py-2 text-xs">
        <div className="flex items-center gap-2">
          <FileText className="h-3.5 w-3.5 text-slate-400" />
          <span className="font-mono text-slate-200">Log · {slug}</span>
          <span className="inline-flex items-center gap-1 text-slate-400">
            <span className={cn("h-1.5 w-1.5 rounded-full", connDot)} />
            {connLabel}
          </span>
          {meta && !meta.exists ? (
            <span className="text-amber-400">file non ancora creato</span>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPaused((p) => !p)}
            className="rounded p-1 text-slate-300 hover:bg-slate-800"
            title={paused ? "Riprendi" : "Pausa"}
          >
            {paused ? (
              <Play className="h-3.5 w-3.5" />
            ) : (
              <Pause className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            onClick={copyAll}
            className="rounded p-1 text-slate-300 hover:bg-slate-800"
            title="Copia tutto"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setLines([])}
            className="rounded p-1 text-slate-300 hover:bg-slate-800"
            title="Pulisci"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div
        ref={scrollRef}
        className="overflow-y-auto font-mono text-[11px] leading-relaxed"
        style={{ height }}
      >
        {lines.length === 0 ? (
          <div className="flex h-full items-center justify-center p-4 text-center text-xs text-slate-400">
            {state === "error" ? (
              <span className="inline-flex items-center gap-2 text-red-400">
                <WifiOff className="h-4 w-4" />
                Errore connessione WebSocket
              </span>
            ) : state === "connecting" ? (
              "Connessione…"
            ) : meta && !meta.exists ? (
              "Il file di log viene creato al primo step eseguito."
            ) : (
              "Nessun log ancora."
            )}
          </div>
        ) : (
          <pre className="whitespace-pre-wrap break-words px-3 py-2">
            {lines.map((l, i) => (
              <div
                key={i}
                className={cn(
                  "px-1",
                  /\bERROR\b/i.test(l)
                    ? "text-red-300"
                    : /\bWARN(ING)?\b/i.test(l)
                      ? "text-amber-300"
                      : /\bINFO\b/i.test(l)
                        ? "text-moving-300"
                        : "text-slate-300",
                )}
              >
                {l}
              </div>
            ))}
          </pre>
        )}
      </div>
    </div>
  );
}
